import { Spinner } from 'react-bootstrap'
import styled, { keyframes } from 'styled-components'
import { Text, Icon, theme } from 'verak-ui'
import { useEffect, useState, createContext, useContext } from 'react'

export const LoadingOverlayContext = createContext()

const initialErrorState = {
	status: false,
	errorMessage: '',
	errorStatusCode: '',
}

export function LoadingOverlayProvider({ children, ...props }) {
	const [isLoading, setIsLoading] = useState(false)
	const [loadingText, setLoadingText] = useState('')

	const [errorState, setErrorState] = useState(initialErrorState)

	const resetError = () => setErrorState(initialErrorState)

	const value = {
		isLoading,
		setIsLoading,
		loadingText,
		setLoadingText,
		errorState,
		setErrorState,
		resetError,
	}

	return (
		<LoadingOverlayContext.Provider {...props} value={value}>
			{children}
		</LoadingOverlayContext.Provider>
	)
}

export const useLoadingOverlay = () => {
	const context = useContext(LoadingOverlayContext)
	if (!context) {
		throw new Error('useLoadingOverlay must be used within LoadingOverlayProvider')
	}
	return context
}

const LoadingOverlay = () => {
	const { isLoading, loadingText, setLoadingText, errorState, resetError } = useLoadingOverlay()

	useEffect(() => {
		if (isLoading) {
			document.body.style.overflow = 'hidden'
		} else {
			document.body.style.overflow = 'auto'
		}

		return () => (document.body.style.overflow = 'auto')
	}, [isLoading])

	let loaderContent = null

	useEffect(() => {
		if (!loadingText) {
			setLoadingText('Loading')
		}
	}, [])

	if (isLoading) {
		loaderContent = (
			<LoaderContainer>
				<Spinner className="mt-2" animation="border" variant="secondary" />

				<Text
					className="mt-3 text-center"
					color={theme.colors.gray2}
					fontSize="16px"
					style={{ maxWidth: '300px' }}
				>
					{loadingText}
				</Text>
			</LoaderContainer>
		)
	} else if (errorState?.status) {
		loaderContent = (
			<LoaderContainer className="">
				<Icon name="warningTriangleSm" color={theme.colors.red} size="50px" />

				<Text className="mt-2" color={theme.colors.red} fontWeight="700" fontSize="18px">
					{errorState?.errorMessage ?? 'An error has occurred'}
				</Text>

				{errorState?.errorStatusCode && (
					<Text className="mt-1 mb-2" color={theme.colors.gray2} fontSize="16px">
						ERROR CODE: {errorState.errorStatusCode || 500}
					</Text>
				)}

				<Text className="my-1 text-center" color={theme.colors.gray3} style={{ maxWidth: '300px' }}>
					If this issue is recurring, please get in touch with support and mention the error code.
				</Text>

				<OkButton
					className="mt-2"
					color={theme.colors.primary}
					fontSize="14px"
					onClick={() => resetError()}
				>
					OK
				</OkButton>
			</LoaderContainer>
		)
	}

	if (loaderContent) {
		return <OverlayContainer>{loaderContent}</OverlayContainer>
	} else {
		return null
	}
}

// animations
const fadeIn = keyframes`
	from {
		opacity: 0;
	}
	to {
		opacity: 1;
	}
`

const OverlayContainer = styled.div`
	background: rgba(0, 0, 0, 0.4);
	position: fixed;
	z-index: 1100;
	width: 100%;
	height: 100%;
	top: 0px;
	/* bottom: 0; */
	left: 0;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	overflow: none;
	animation: ${fadeIn} 0.3s ease-in-out;
`

const LoaderContainer = styled.div`
	margin: auto;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	background: #fff;
	padding: 1rem;
	${'' /* width: min(200px, 100%); */}
	border-radius: 8px;
`

const OkButton = styled(Text)`
	padding: 3px 5px;
	border-radius: 4px;
	cursor: pointer;
	&:hover {
		background: #cfcfcf;
	}
`

export default LoadingOverlay
